import { ConfigProvider } from "antd";
import "antd/dist/reset.css";
import "./theme/theme-bridge.css";
import React, { useEffect, useRef } from "react";
import { getAppAntdTheme } from "./theme/antdTheme";
import { Provider, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ProjectName } from "./config";
import { persistor, store } from "./redux/store";
import Routers from "./routers";
import GlobalStyles from "./styles/GlobalStyles";
import UserThemeSync from "./components/UserThemeSync";
import { ThemeProvider, useThemeMode } from "./contexts/ThemeContext";

/**
 * PENDO INITIALIZATION WRAPPER
 * This component listens to the Redux store. When a user logs in,
 * it passes their identity to Pendo.
 */
function PendoWrapper({ children }) {
  // Grab user data from Redux state
  const userData = useSelector(
    (state) =>
      state.user?.data?.user ||
      state.user?.user ||
      state.session?.data?.user
  );
  const pendoInitializedRef = useRef(false);

  useEffect(() => {
    if (!userData) return;

    const visitorId = userData.user_data?.id || userData.id;
    if (!visitorId) return;

    const payload = {
      visitor: {
        id: visitorId, // REQUIRED: Must be 'id'
        email: userData.user_data?.email,
        role: userData.user_data?.user_type,
        full_name: userData.user_data?.full_name,
      },
      account: {
        // Optional: If you have company data, map it here
        id: userData.user_data?.company_id || "INDIVIDUAL",
        name: userData.user_data?.company_name || "Personal Account",
      },
    };

    const runPendo = () => {
      if (!window.pendo || typeof window.pendo.initialize !== "function") return false;

      if (!pendoInitializedRef.current) {
        window.pendo.initialize(payload);
        pendoInitializedRef.current = true;
      } else if (typeof window.pendo.identify === "function") {
        window.pendo.identify(payload.visitor, payload.account);
      }

      return true;
    };

    // Try immediately first.
    if (runPendo()) return;

    // If Pendo script is still loading, retry briefly until it's available.
    const intervalId = window.setInterval(() => {
      if (runPendo()) {
        window.clearInterval(intervalId);
      }
    }, 300);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [userData]); // Re-run whenever the user logs in or out

  return children;
}

function App() {
  document.title = ProjectName;

  const handleLoad = () => {
    console.log("Page fully loaded including stylesheets, images, etc.");
  };

  useEffect(() => {
    window.addEventListener("load", handleLoad);
    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  const AppWithTheme = () => {
    const { themeMode } = useThemeMode();
    return (
      <ConfigProvider theme={getAppAntdTheme(themeMode)}>
        <GlobalStyles />
        <Routers />
      </ConfigProvider>
    );
  };

  return (
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {/* We wrap the app logic with PendoWrapper so it has access to Redux state */}
          <PendoWrapper>
          <ThemeProvider>
            <UserThemeSync />
            <AppWithTheme />
          </ThemeProvider>
          </PendoWrapper>
        </PersistGate>
      </Provider>
  );
}

export default App;